import React, { useEffect, useState, useRef, useCallback } from 'react';
import io from 'socket.io-client';
import axios from 'axios'; // Import axios for making HTTP requests
import { toast } from 'react-toastify'; // Consider adding react-toastify for notifications
import 'react-toastify/dist/ReactToastify.css';

import ArrowUpwardIcon from '@mui/icons-material/ArrowUpward'; // For scroll to top
import ArrowDownwardIcon from '@mui/icons-material/ArrowDownward'; // For scroll to bottom
import InsertPhotoIcon from '@mui/icons-material/InsertPhoto'; // Icon for image upload
import CloseIcon from '@mui/icons-material/Close'; // Icon for removing image preview

const API_BASE_URL =
  process.env.NODE_ENV === 'production'
    ? process.env.REACT_APP_API_URL_PRODUCTION
    : process.env.REACT_APP_API_URL;

const MAX_CONSECUTIVE_MESSAGES = 5;
const SCROLL_THRESHOLD = 100; // Pixels from bottom to show "scroll to bottom" button

const WebstoreUserChat = ({ user }) => {
  const socketRef = useRef(null);
  const [messages, setMessages] = useState([]);
  const [newMsg, setNewMsg] = useState('');
  const [selectedImage, setSelectedImage] = useState(null); // State for selected image file
  const [imagePreviewUrl, setImagePreviewUrl] = useState(null); // State for image preview URL
  const [isAdminTyping, setIsAdminTyping] = useState(false);
  const [adminOnline, setAdminOnline] = useState(false);
  const [consecutiveUserMessages, setConsecutiveUserMessages] = useState(0);
  const messagesEndRef = useRef(null); // Ref for auto-scrolling to the latest message
  const chatBodyRef = useRef(null); // Ref for the scrollable chat area
  const typingTimeout = useRef(null);
  const sentMsgTracker = useRef(new Set()); // To prevent duplicate messages from socket re-emits
  const [showScrollToBottom, setShowScrollToBottom] = useState(false); // New state for scroll button
  const fileInputRef = useRef(null); // Ref for file input element
  const [isUploadingImage, setIsUploadingImage] = useState(false); // NEW: To manage loading state during image upload

  // Function to format message timestamps
  const formatTimestamp = (timestamp) => {
    if (!timestamp) return '';
    const date = new Date(timestamp);
    const now = new Date();

    const isToday = date.toDateString() === now.toDateString();
    // Reset 'now' for yesterday check to avoid modifying the original 'now' object
    const yesterday = new Date();
    yesterday.setDate(yesterday.getDate() - 1);
    const isYesterday = yesterday.toDateString() === date.toDateString();

    if (isToday) {
      return date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
    } else if (isYesterday) {
      return `Yesterday ${date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}`;
    } else {
      return date.toLocaleString([], {
        month: 'short',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
      });
    }
  };

  // Effect for Socket.IO connection and event listeners
  useEffect(() => {
    if (!user?._id) return;

    // Initialize socket if not already connected
    if (!socketRef.current) {
      socketRef.current = io(API_BASE_URL, { withCredentials: true });
    }

    const socket = socketRef.current;
    socket.emit('join', { userId: user._id, isAdmin: false });

    // Handle incoming chat history
    const handleChatHistory = (history) => {
      const uniqueMessages = [];
      const seen = new Set();
      for (let msg of history) {
        // Use a more robust key to identify unique messages (especially if _id isn't immediately available)
        const key = msg._id || `${msg.sender}-${msg.createdAt}-${msg.message || ''}-${msg.imageUrl || ''}`;
        if (!seen.has(key)) {
          seen.add(key);
          uniqueMessages.push(msg);
        }
      }
      setMessages(uniqueMessages);

      let count = 0;
      for (let i = uniqueMessages.length - 1; i >= 0; i--) {
        if (uniqueMessages[i].sender === 'admin') break;
        if (uniqueMessages[i].sender === 'user') count++;
      }
      setConsecutiveUserMessages(count);
    };

    // Handle new incoming messages
    const handleNewMessage = (msg) => {
      const key = msg._id || `${msg.sender}-${msg.createdAt}-${msg.message || ''}-${msg.imageUrl || ''}`;
      if (sentMsgTracker.current.has(key)) {
        sentMsgTracker.current.delete(key);
        return;
      }

      setMessages((prev) => {
        const exists = prev.some((m) =>
          (m._id && m._id === msg._id) ||
          (
            m.sender === msg.sender &&
            m.createdAt === msg.createdAt &&
            m.message === msg.message && // Compare text message content
            m.imageUrl === msg.imageUrl   // Compare image URL content
          )
        );
        return exists ? prev : [...prev, msg];
      });

      setConsecutiveUserMessages((prev) => {
        if (msg.sender === 'admin') return 0;
        return msg.sender === 'user' ? prev + 1 : prev;
      });

      if (msg.sender === 'admin') {
        socket.emit('mark-read', { userId: user._id });
      }
    };

    // Handle typing status from admin
    const handleTypingStatus = ({ isTyping }) => {
      setIsAdminTyping(isTyping);
    };

    // Handle message read confirmation from admin
    const handleMessageRead = () => {
      setMessages((prev) =>
        prev.map((msg) =>
          msg.sender === 'user' ? { ...msg, read: true } : msg
        )
      );
    };

    // Handle online status of other users (specifically admin)
    const handleUserOnline = (onlineIds) => {
      setAdminOnline(onlineIds.includes('admin'));
    };

    // Register socket event listeners
    socket.on('chat-history', handleChatHistory);
    socket.on('new-message', handleNewMessage);
    socket.on('typing', handleTypingStatus);
    socket.on('message-read-confirmed', handleMessageRead);
    socket.on('user-online', handleUserOnline);

    // Cleanup function: remove listeners and disconnect socket
    return () => {
      socket.off('chat-history', handleChatHistory);
      socket.off('new-message', handleNewMessage);
      socket.off('typing', handleTypingStatus);
      socket.off('message-read-confirmed', handleMessageRead);
      socket.off('user-online', handleUserOnline);
      if (socketRef.current) {
        socketRef.current.disconnect();
        socketRef.current = null;
      }
      clearTimeout(typingTimeout.current);
    };
  }, [user]);

  // Function to send a message (text or image)
  const handleSend = async () => { // Made async for axios call
    if (!user?._id) return;

    // Determine if there's anything to send
    const hasText = newMsg.trim().length > 0;
    const hasImage = selectedImage !== null;

    if (!hasText && !hasImage) {
      // Nothing to send
      return;
    }

    // Prevent sending if consecutive message limit is reached AND no image is selected
    // OR if we're already uploading an image
    if ((consecutiveUserMessages >= MAX_CONSECUTIVE_MESSAGES && !hasImage) || isUploadingImage) {
      return;
    }

    const socket = socketRef.current;
    const now = new Date().toISOString();

    if (hasImage) {
      setIsUploadingImage(true); // Start loading state
      try {
        const formData = new FormData();
        // The 'photo' key must match the field name your image controller expects (e.g., in multer config)
        formData.append('photo', selectedImage);
        // Add name and description for the image controller if required, or simple default
        formData.append('name', `chat-image-${Date.now()}`); // Unique name
        formData.append('description', 'Chat image');
        formData.append('tags', JSON.stringify(['chat'])); // Default tags
        formData.append('imageCategories', JSON.stringify([])); // No specific categories for chat images usually

        // NEW: Make an HTTP POST request to your existing image upload endpoint
        const uploadRes = await axios.post(`${API_BASE_URL}/api/images`, formData, {
          headers: {
            'Content-Type': 'multipart/form-data',
          },
          withCredentials: true,
        });

        const uploadedImageUrl = uploadRes.data.url; // Assuming your controller returns { url: '...' }

        // Now, send the message (which includes the new image URL) via Socket.IO
        const messageObj = {
          userId: user._id,
          imageUrl: uploadedImageUrl, // Use the URL obtained from your image controller
          message: newMsg.trim() || undefined, // Include text as caption, or undefined if empty
          sender: 'user',
          createdAt: now,
        };

        const key = `${messageObj.sender}-${messageObj.createdAt}-${messageObj.imageUrl || ''}-${messageObj.message || ''}`;
        sentMsgTracker.current.add(key);

        socket.emit('send-message', messageObj); // Use the general 'send-message' event

        toast.success('Image sent!'); // Notification for successful send

        // Clear local states after successful send
        setSelectedImage(null);
        setImagePreviewUrl(null);
        if (fileInputRef.current) {
          fileInputRef.current.value = ''; // Reset file input
        }
        setNewMsg(''); // Clear text input
      } catch (error) {
        console.error('Error uploading image or sending chat message:', error);
        toast.error('Failed to send image.'); // User-friendly error
      } finally {
        setIsUploadingImage(false); // End loading state
      }
    } else if (hasText) {
      // Existing logic for sending text message
      const messageObj = {
        userId: user._id,
        message: newMsg.trim(),
        sender: 'user',
        createdAt: now,
      };

      const key = `${messageObj.sender}-${messageObj.createdAt}-${messageObj.message}`;
      sentMsgTracker.current.add(key);

      socket.emit('send-message', messageObj); // Emit text message
      setNewMsg(''); // Clear text input field
    }
  };

  // Handle image file selection
  const handleImageChange = (e) => {
    const file = e.target.files[0];
    if (file && file.type.startsWith('image/')) {
      setSelectedImage(file);
      setImagePreviewUrl(URL.createObjectURL(file));
      // No longer clearing newMsg here; newMsg will act as a caption
    } else {
      setSelectedImage(null);
      setImagePreviewUrl(null);
      if (fileInputRef.current) {
        fileInputRef.current.value = ''; // Reset file input if invalid type
      }
      toast.error('Please select an image file.');
    }
  };

  // Remove selected image preview
  const removeImage = () => {
    setSelectedImage(null);
    setImagePreviewUrl(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = ''; // Reset file input
    }
  };

  // Handle input change and emit typing status
  const handleTypingChange = (e) => {
    setNewMsg(e.target.value);
    // Don't clear selectedImage or imagePreviewUrl here, as text input can be a caption

    const socket = socketRef.current;
    socket.emit('typing', { userId: user._id, isTyping: true });

    clearTimeout(typingTimeout.current);
    typingTimeout.current = setTimeout(() => {
      socket.emit('typing', {
        userId: user._id,
        isTyping: false,
      });
    }, 1000); // Stop typing after 1 second of no input
  };

  // Function to scroll to the very bottom of the chat
  const scrollToBottom = useCallback(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, []);

  // Effect to scroll to bottom on new messages, but only if user is already near bottom
  useEffect(() => {
    const chatBody = chatBodyRef.current;
    if (chatBody) {
      const isAtBottom = chatBody.scrollHeight - chatBody.clientHeight <= chatBody.scrollTop + SCROLL_THRESHOLD;
      if (isAtBottom || messages.length <= 1) {
        scrollToBottom();
      }
    }
  }, [messages, scrollToBottom]);

  // Effect to manage "scroll to bottom" button visibility
  useEffect(() => {
    const chatBody = chatBodyRef.current;
    if (!chatBody) return;

    const handleScroll = () => {
      const { scrollTop, scrollHeight, clientHeight } = chatBody;
      setShowScrollToBottom(scrollHeight - clientHeight > scrollTop + SCROLL_THRESHOLD);
    };

    chatBody.addEventListener('scroll', handleScroll);
    handleScroll(); // Initial check

    return () => {
      chatBody.removeEventListener('scroll', handleScroll);
    };
  }, [messages]);


  return (
    <div className="webstore-chat-wrapper user-chat">
      <div className="chat-header">
        <span className="admin-name">
          NavarroJalen
          {adminOnline && <span className="online-indicator">● Online</span>}
        </span>
        {showScrollToBottom && (
          <button className="scroll-to-bottom-btn" onClick={scrollToBottom} aria-label="Scroll to latest message">
            <ArrowDownwardIcon />
          </button>
        )}
      </div>

      <div className="chat-content">
        <div className="chat-body" ref={chatBodyRef}>
          {messages.length === 0 ? (
            <div className="no-messages-placeholder">
              Start a chat with NavarroJalen!
              {adminOnline && <span className="online-indicator-text">NavarroJalen is online.</span>}
            </div>
          ) : (
            messages.map((msg, idx) => {
              const isUser = msg.sender === 'user';
              const lastUserMessageIndex = messages.map(m => m.sender).lastIndexOf('user');
              const isLastUserMessage = isUser && idx === lastUserMessageIndex;

              return (
                <div key={msg._id || `${msg.sender}-${idx}-${msg.createdAt}`} className={`chat-message ${isUser ? 'right' : 'left'}`}>
                  <div className="chat-bubble">
                    {msg.message && <p className="chat-text">{msg.message}</p>}
                    {msg.imageUrl && (
                      <div className="chat-image-container">
                        <img src={msg.imageUrl} alt="Sent" className="chat-image" />
                      </div>
                    )}
                    <div className="chat-meta">
                      <span className="chat-time">{formatTimestamp(msg.createdAt)}</span>
                    </div>
                  </div>
                  {isLastUserMessage && msg.read && (
                    <div className="chat-seen-indicator">✓✓ Seen</div>
                  )}
                </div>
              );
            })
          )}

          {isAdminTyping && (
            <div className="typing-indicator left">
              <div className="typing-bubble">
                <span className="dot" />
                <span className="dot" />
                <span className="dot" />
              </div>
            </div>
          )}
          <div ref={messagesEndRef} />
        </div>

        {selectedImage && imagePreviewUrl && ( // Render preview only if an image is selected
          <div className="image-send-preview">
            <img src={imagePreviewUrl} alt="Preview" />
            <button className="remove-image-btn" onClick={removeImage} aria-label="Remove image">
              <CloseIcon />
            </button>
          </div>
        )}

        <div className="chat-input-area">
          <input
            type="text"
            placeholder={
              isUploadingImage ? 'Uploading image...' : // Show uploading status
              (consecutiveUserMessages >= MAX_CONSECUTIVE_MESSAGES && !selectedImage)
                ? 'Please wait for admin to reply...'
                : (selectedImage ? 'Add a caption (optional)' : 'Type a message...')
            }
            value={newMsg}
            onChange={handleTypingChange}
            onKeyDown={(e) =>
              e.key === 'Enter' &&
              (newMsg.trim() || selectedImage) &&
              consecutiveUserMessages < MAX_CONSECUTIVE_MESSAGES &&
              !isUploadingImage && // Prevent sending while image is uploading
              handleSend()
            }
            disabled={
              (consecutiveUserMessages >= MAX_CONSECUTIVE_MESSAGES && !selectedImage) ||
              isUploadingImage // Disable input if currently uploading
            }
            aria-label="Type your message"
          />
          <input
            type="file"
            accept="image/*"
            ref={fileInputRef}
            style={{ display: 'none' }}
            onChange={handleImageChange}
            disabled={
              consecutiveUserMessages >= MAX_CONSECUTIVE_MESSAGES ||
              isUploadingImage // Disable file input if currently uploading
            }
          />
          <button
            className="attach-image-btn"
            onClick={() => {
              if (fileInputRef.current) {
                fileInputRef.current.click();
              }
            }}
            disabled={
              consecutiveUserMessages >= MAX_CONSECUTIVE_MESSAGES ||
              isUploadingImage // Disable attach button if currently uploading
            }
            aria-label="Attach image"
            title="Attach Image"
          >
            <InsertPhotoIcon />
          </button>
          <button
            onClick={handleSend}
            disabled={
              (consecutiveUserMessages >= MAX_CONSECUTIVE_MESSAGES && !selectedImage) ||
              (!newMsg.trim() && !selectedImage) ||
              isUploadingImage // Disable send button if nothing to send or if uploading
            }
            aria-label="Send message"
          >
            {isUploadingImage ? 'Sending...' : 'Send'} {/* Change button text during upload */}
          </button>
        </div>
      </div>
    </div>
  );
};

export default WebstoreUserChat;